import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../../shared/context/UserContext';
import { getSites } from '../api/inventoryApi';
import './InventoryScreen.css';

/* Build display name: "Site Name (City)" or "Site Name (site_code)" */
function displayName(site) {
  if (site.site_city) return `${site.site_name} (${site.site_city})`;
  if (site.supplier_site_code) return `${site.site_name} (${site.supplier_site_code})`;
  return site.site_name || 'Unnamed Site';
}

/**
 * InventoryScreen
 * Props:
 *   mode — 'disposal' | 'lifecycle' (passed from branch route)
 */
export default function InventoryScreen({ mode = 'disposal', sectionLabel }) {
  const [siteSearch, setSiteSearch] = useState('');
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { callerEmail } = useUser();

  /* ── Load sites on mount ────────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getSites(callerEmail)
      .then(data => {
        if (!cancelled) setSites(data || []);
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Failed to load sites');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [callerEmail]);

  /* ── Filter sites by search ─────────────────────────────────────────── */
  const filteredSites = useMemo(() => {
    if (!siteSearch.trim()) return sites;
    const q = siteSearch.toLowerCase();
    return sites.filter(s =>
      (s.site_name || '').toLowerCase().includes(q) ||
      (s.site_city || '').toLowerCase().includes(q) ||
      (s.oem_primary || '').toLowerCase().includes(q) ||
      (s.supplier_site_code || '').toLowerCase().includes(q)
    );
  }, [siteSearch, sites]);

  function handleSiteClick(site) {
    // Navigate relative to current branch path (e.g. /disposal/inventory/SITE_ID)
    const basePath = location.pathname.replace(/\/$/, '');
    navigate(`${basePath}/${site.site_id}`, {
      state: { mode, siteName: displayName(site) },
    });
  }

  const subtitle = sectionLabel ?? (mode === 'lifecycle' ? 'Lifecycle Review' : 'Disposal Review');

  return (
    <div className="inv-screen">
      {/* ── Header ──────────────────────────────────────── */}
      <header className="inv-header">
        <div>
          <h1 className="inv-header-title">Field Inventory</h1>
          <p className="inv-header-sub">{subtitle}</p>
        </div>
      </header>

      {/* ── Search Bar ──────────────────────────────────── */}
      <div className="inv-search-bar">
        <svg className="inv-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <input
          type="text"
          className="inv-search-input"
          placeholder="Filter sites..."
          value={siteSearch}
          onChange={(e) => setSiteSearch(e.target.value)}
        />
      </div>

      {/* ── Site List ───────────────────────────────────── */}
      <div className="inv-site-list">
        {loading && (
          <div className="inv-empty">Loading sites...</div>
        )}

        {!loading && error && (
          <div className="inv-error">
            <p className="inv-error-message">Failed to load sites</p>
            <p className="inv-error-detail">{error}</p>
            <button
              className="inv-error-retry"
              onClick={() => {
                setError(null);
                setLoading(true);
                getSites(callerEmail)
                  .then(data => setSites(data || []))
                  .catch(err => setError(err.message))
                  .finally(() => setLoading(false));
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && filteredSites.length === 0 && (
          <div className="inv-empty">
            {siteSearch.trim() ? 'No sites match your search.' : 'No sites found.'}
          </div>
        )}

        {!loading && !error && filteredSites.map(site => (
          <button
            key={site.site_id}
            className="inv-site-card"
            onClick={() => handleSiteClick(site)}
          >
            <div className="inv-site-top">
              <span className="inv-site-name">{displayName(site)}</span>
            </div>
            <div className="inv-site-meta">
              <span className="inv-site-oem">{site.oem_primary || 'Unknown OEM'}</span>
              {site.site_state_province && (
                <span className="inv-site-activity">{site.site_state_province}</span>
              )}
            </div>
          </button>
        ))}

        {!loading && !error && (
          <div className="inv-site-card inv-site-card-placeholder">
            <span className="inv-placeholder-icon">📋</span>
            <span className="inv-placeholder-text">Site Index — Coming Soon</span>
          </div>
        )}
      </div>
    </div>
  );
}
